import { useState, useEffect, useCallback } from 'react';
import api, { urlDocument } from 'services/api';
import { apercuDocument } from 'services/documents';
import BoutonTelechargerDocument from 'components/shared/BoutonTelechargerDocument';
import IAAnalysisPanel from 'components/shared/IAAnalysisPanel';

const MOIS_LABELS = [
  '', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'
];

const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const CURRENT_MONTH = now.getMonth() + 1;

const fmt = (val) => Number(val || 0).toLocaleString() + ' FCFA';

// Config heures sup (mirrors regles-travail.js)
const HS_TAUX_41_48 = 0.15;
const HS_TAUX_AU_DELA = 0.35;
const HEURES_SEMAINE = 40;

function calcMontantHS(salaireBase, nbHeures) {
  if (!nbHeures || nbHeures <= 0 || !salaireBase) return 0;
  const tauxHoraire = salaireBase / (HEURES_SEMAINE * 4.33);
  const h1 = Math.min(nbHeures, 8);
  const h2 = Math.max(0, nbHeures - 8);
  return Math.round((h1 * tauxHoraire * (1 + HS_TAUX_41_48) + h2 * tauxHoraire * (1 + HS_TAUX_AU_DELA)) * 100) / 100;
}

const ALERT_TYPE_LABELS = {
  essai: 'Fin periode d\'essai',
  cdd_expire: 'CDD expirant',
  smig: 'Salaire sous SMIG',
  cdd_duree: 'CDD > 24 mois',
};

export default function Paie() {
  const [tab, setTab] = useState('bulletins');
  const [mois, setMois] = useState(CURRENT_MONTH);
  const [annee, setAnnee] = useState(CURRENT_YEAR);

  // Tab 1 state
  const [bulletins, setBulletins] = useState([]);
  const [loadingBulletins, setLoadingBulletins] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Tab 2 state
  const [livre, setLivre] = useState(null);
  const [loadingLivre, setLoadingLivre] = useState(false);

  // Tab 3 state
  const [cnss, setCnss] = useState(null);
  const [loadingCnss, setLoadingCnss] = useState(false);

  // Tab 4 state - Alertes RH
  const [alertes, setAlertes] = useState([]);
  const [loadingAlertes, setLoadingAlertes] = useState(false);
  const [alerteCount, setAlerteCount] = useState(0);

  // Edit modal
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({
    nb_heures_sup: 0, primes: 0, deductions: 0, panier_nuit: 0, prime_risque: 0, prime_anciennete: 0, prime_nuit: 0
  });

  const [error, setError] = useState('');
  const [explainBulletin, setExplainBulletin] = useState(null);
  const [simulationBefore, setSimulationBefore] = useState(null);

  const fetchBulletins = useCallback(async () => {
    setLoadingBulletins(true);
    setError('');
    try {
      const res = await api.get(`/paie/bulletins?mois=${mois}&annee=${annee}`);
      setBulletins(Array.isArray(res.data) ? res.data : res.data.bulletins || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur chargement bulletins');
      setBulletins([]);
    } finally {
      setLoadingBulletins(false);
    }
  }, [mois, annee]);

  const fetchLivre = useCallback(async () => {
    setLoadingLivre(true);
    setError('');
    try {
      const res = await api.get(`/paie/livre?mois=${mois}&annee=${annee}`);
      setLivre(res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur chargement livre de paie');
      setLivre(null);
    } finally {
      setLoadingLivre(false);
    }
  }, [mois, annee]);

  const fetchCnss = useCallback(async () => {
    setLoadingCnss(true);
    setError('');
    try {
      const res = await api.get(`/paie/cnss?mois=${mois}&annee=${annee}`);
      setCnss(res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur chargement declaration CNSS');
      setCnss(null);
    } finally {
      setLoadingCnss(false);
    }
  }, [mois, annee]);

  const fetchAlertes = useCallback(async () => {
    setLoadingAlertes(true);
    setError('');
    try {
      const res = await api.get('/paie/alertes');
      const data = Array.isArray(res.data) ? res.data : [];
      setAlertes(data);
      setAlerteCount(data.length);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur chargement alertes RH');
      setAlertes([]);
    } finally {
      setLoadingAlertes(false);
    }
  }, []);

  // Fetch alerte count on mount
  useEffect(() => {
    api.get('/paie/alertes').then(res => {
      const data = Array.isArray(res.data) ? res.data : [];
      setAlerteCount(data.length);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'bulletins') fetchBulletins();
    else if (tab === 'livre') fetchLivre();
    else if (tab === 'cnss') fetchCnss();
    else if (tab === 'alertes') fetchAlertes();
  }, [tab, mois, annee, fetchBulletins, fetchLivre, fetchCnss, fetchAlertes]);

  const handleGenerate = async () => {
    if (!window.confirm(`Generer les bulletins de ${MOIS_LABELS[mois]} ${annee} ?`)) return;
    setGenerating(true);
    setError('');
    try {
      await api.post('/paie/generate', { mois, annee });
      fetchBulletins();
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur generation bulletins');
    } finally {
      setGenerating(false);
    }
  };

  // ── IMPRIMER — INDEPENDANT DE « PAYER » ──────────────────────────────────
  // Premier pas de la separation generer / imprimer / valider / marquer paye :
  // on n'a plus besoin de declarer un bulletin paye pour le voir.
  // Disponible sur TOUS les etats, brouillon compris : le brouillon se denonce
  // lui-meme par son bandeau et son filigrane, imprimes sur le document.
  // Le telechargement passe par l'endpoint authentifie (verification du
  // tenant_id en base), jamais par un montage statique.
  const [impression, setImpression] = useState(null);
  const handlePrint = async (id) => {
    setImpression(id);
    try {
      await api.post(`/paie/bulletins/${id}/pdf`);           // produit le PDF
      // Apercu via le helper partage : meme onglet, meme comportement. Le NOM
      // de fichier n'est jamais compose ici — il vient de l'en-tete du serveur,
      // et seul le bouton « Telecharger » le livre (une blob: URL n'est pas
      // nommable). Voir services/documents.js.
      await apercuDocument(urlDocument('bulletin', id));
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Impression impossible');
    } finally { setImpression(null); }
  };

  const handleValidate = async (id) => {
    if (!window.confirm('Valider ce bulletin ?')) return;
    try {
      await api.put(`/paie/bulletins/${id}/valider`);
      fetchBulletins();
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur validation');
    }
  };

  const handlePay = async (id) => {
    if (!window.confirm('Marquer ce bulletin comme paye ?')) return;
    try {
      await api.put(`/paie/bulletins/${id}/payer`);
      fetchBulletins();
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur paiement');
    }
  };

  const openEdit = (b) => {
    setEditModal(b);
    setEditForm({
      nb_heures_sup: b.nb_heures_sup || 0,
      primes: b.primes || 0,
      deductions: b.deductions || 0,
      panier_nuit: b.panier_nuit || 0,
      prime_risque: b.prime_risque || 0,
      prime_anciennete: b.prime_anciennete || 0,
      prime_nuit: b.prime_nuit || 0,
    });
    setSimulationBefore({
      salaire_base: Number(b.salaire_base), nb_heures_sup: Number(b.nb_heures_sup || 0),
      montant_heures_sup: Number(b.montant_heures_sup || 0), primes: Number(b.primes || 0),
      prime_risque: Number(b.prime_risque || 0), prime_nuit: Number(b.prime_nuit || 0),
      prime_anciennete: Number(b.prime_anciennete || 0), panier_nuit: Number(b.panier_nuit || 0),
      deductions: Number(b.deductions || 0), salaire_brut: Number(b.salaire_brut || 0),
      cnss_employe: Number(b.cnss_employe || 0), net_imposable: Number(b.net_imposable || 0),
    });
  };

  const [simulationAfter, setSimulationAfter] = useState(null);

  const handleEditSave = async () => {
    try {
      const res = await api.put(`/paie/bulletins/${editModal.id}`, {
        nb_heures_sup: Number(editForm.nb_heures_sup),
        primes: Number(editForm.primes),
        deductions: Number(editForm.deductions),
        panier_nuit: Number(editForm.panier_nuit),
        prime_risque: Number(editForm.prime_risque),
        prime_anciennete: Number(editForm.prime_anciennete),
        prime_nuit: Number(editForm.prime_nuit),
      });
      const updated = res.data;
      setSimulationAfter({
        salaire_base: Number(updated.salaire_base), nb_heures_sup: Number(updated.nb_heures_sup || 0),
        montant_heures_sup: Number(updated.montant_heures_sup || 0), primes: Number(updated.primes || 0),
        prime_risque: Number(updated.prime_risque || 0), prime_nuit: Number(updated.prime_nuit || 0),
        prime_anciennete: Number(updated.prime_anciennete || 0), panier_nuit: Number(updated.panier_nuit || 0),
        deductions: Number(updated.deductions || 0), salaire_brut: Number(updated.salaire_brut || 0),
        cnss_employe: Number(updated.cnss_employe || 0), net_imposable: Number(updated.net_imposable || 0),
      });
      setEditModal({ ...editModal, ...updated });
      fetchBulletins();
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur modification');
    }
  };

  // Computed stats for bulletins
  const stats = {
    masse: bulletins.reduce((s, b) => s + Number(b.net_imposable || 0), 0),
    brouillon: bulletins.filter((b) => b.statut === 'brouillon').length,
    valide: bulletins.filter((b) => b.statut === 'valide').length,
    paye: bulletins.filter((b) => b.statut === 'paye').length,
  };

  /* ═══════════════════════════════════════════════════════════════════════
   * A QUEL TITRE LA PIECE A-T-ELLE ETE POSEE ? (file 50, 16/09/2026)
   * ═══════════════════════════════════════════════════════════════════════
   * ⚠️ UN ETAT SE NOMME, IL NE S AFFICHE PAS EN BLANC (decision de Dom).
   *    Les pieces anterieures au lot ne portent AUCUN acte, et c est VOULU :
   *    deduire le role depuis les roles ACTUELS de l auteur produirait une
   *    valeur que personne n a decidee et qui aurait l air vraie.
   *    ➡️ On ecrit « Anteriorite indeterminee », comme « prix a definir ».
   *       Un blanc laisserait croire a une donnee manquante par accident.
   * ⓘ PLUSIEURS ROLES = CUMUL DE POSTES. On l affiche, on ne le qualifie pas
   *   (regle 62) : ni « attention », ni « conflit », ni couleur d alerte.
   *   Le cumul est le mode de fonctionnement courant de la cible (regle 64). */
  const titreDeLActe = (b) => {
    const actes = Array.isArray(b.actes) ? b.actes : [];
    if (actes.length === 0) {
      return (
        <span style={{ color: 'var(--gss-text-light)', fontStyle: 'italic' }}
              title="Piece emise avant que le produit n enregistre le titre de l acte. Rien n est reconstitue : un document emis ne se reecrit jamais.">
          Antériorité indéterminée
        </span>
      );
    }
    const dernier = actes[actes.length - 1];
    const roles = Array.isArray(dernier.roles) ? dernier.roles : [];
    return (
      <span title={`Acte : ${dernier.acte}`}>
        {roles.join(' et ')}
        {roles.length > 1 && (
          <span style={{ color: 'var(--gss-text-light)', marginLeft: 6 }}>(cumul)</span>
        )}
      </span>
    );
  };

  const statutBadge = (statut) => {
    if (statut === 'paye') return 'gss-badge gss-badge-green';
    if (statut === 'valide') return 'gss-badge gss-badge-orange';
    return 'gss-badge gss-badge-grey';
  };

  // HS preview for edit modal
  const editSalaireBase = Number(editModal?.salaire_base || 0);
  const editNbHS = Number(editForm.nb_heures_sup || 0);
  const editMontantHS = calcMontantHS(editSalaireBase, editNbHS);
  const editTauxHoraire = editSalaireBase > 0 ? Math.round(editSalaireBase / (HEURES_SEMAINE * 4.33)) : 0;

  // Month/year selectors (shared)
  const renderSelectors = () => (
    <div className="gss-toolbar" style={{ gap: 8, flexWrap: 'wrap' }}>
      <select className="gss-input" value={mois} onChange={(e) => setMois(Number(e.target.value))} style={{ width: 160 }}>
        {Array.from({ length: 12 }, (_, i) => (
          <option key={i + 1} value={i + 1}>{MOIS_LABELS[i + 1]}</option>
        ))}
      </select>
      <select className="gss-input" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} style={{ width: 110 }}>
        {Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i).map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );

  // ==================== TAB 1: BULLETINS ====================
  /* Les constats d'un bulletin, tels que le moteur les a poses a l'emission.
   * ⚠️ AUCUN REPLI, AUCUNE INVENTION : `reserves` peut arriver en tableau
   *    (JSONB), en chaine (pilote qui ne deserialise pas), ou absente sur un
   *    bulletin de l'ancien moteur. On rend un tableau VIDE dans tous les cas
   *    douteux plutot que d'afficher un objet brut a un operateur.
   * ⓘ La lecture est defensive, pas indulgente : elle ne fabrique aucun
   *   constat, elle se contente de ne pas casser l'ecran. */
  /* ── LE DÉTAIL PAR STATUT, LU SUR LA PIÈCE (lot B-bis, 07/10/2026) ─────────
   * 🎯 CE QUI MANQUAIT : la 088 écrit les jours retenus et la base, la 090 y
   *    ajoute `jours_par_statut` — **aucun écran ne les affichait**. L'opérateur
   *    voyait une base réduite sans savoir quels jours avaient été retirés, et
   *    devait ouvrir le PDF un par un pour le découvrir.
   * ⛔ CET ÉCRAN N'AFFIRME AUCUN EFFET DE PAIE. Il compte des journées par
   *    statut, telles que la pièce les porte. *Écrire « Absent (déduit) » ici
   *    réaffirmerait en dur ce que le référentiel du pays décide* — même règle
   *    qu'à l'écran de pointage et sur le bulletin.
   * ⛔ AUCUN REPLI, AUCUNE RECONSTITUTION. Une pièce antérieure au lot ne porte
   *    rien : la cellule dit « — ». *Recalculer un détail de jours sur une pièce
   *    déjà émise inventerait un fait sur un document opposable.*
   * ⓘ Lecture défensive comme `constatsDe` : la colonne peut arriver en objet
   *   (JSONB) ou en chaîne selon le pilote. */
  const LIB_STATUT = {
    present: 'Présent', repos: 'Repos', retard: 'Retard', absent: 'Absent',
    conge: 'Congé', maladie: 'Maladie', demission: 'Démission',
  };
  const joursDe = (b) => {
    let p = b && b.jours_par_statut;
    if (typeof p === 'string') {
      try { p = JSON.parse(p); } catch (e) { p = null; }
    }
    const detail = p && typeof p === 'object'
      ? Object.entries(p).filter(([, n]) => Number(n) > 0)
        /* ⓘ Un statut que cet écran ne sait pas nommer s'affiche TEL QUEL,
         *   jamais écarté : la pièce porte ce qui a servi au calcul. */
        .map(([k, n]) => `${LIB_STATUT[k] || k} ${n}`)
      : [];
    const payes = b && (b.jours_payes === null || b.jours_payes === undefined)
      ? null : Number(b.jours_payes);
    const base = b && (b.base_prorata_jours === null || b.base_prorata_jours === undefined)
      ? null : Number(b.base_prorata_jours);
    const nonPayes = b && (b.jours_absence_non_payes === null
      || b.jours_absence_non_payes === undefined) ? null : Number(b.jours_absence_non_payes);
    return { detail, payes, base, nonPayes, lus: b ? b.pointages_lus : null };
  };

  const constatsDe = (b) => {
    let r = b && b.reserves;
    if (typeof r === 'string') {
      try { r = JSON.parse(r); } catch (e) { return []; }
    }
    if (!Array.isArray(r)) return [];
    return r.filter(x => x && typeof x.message === 'string' && x.message.trim() !== '');
  };

  const renderBulletins = () => (
    <>
      {/* ⚠️ PERIMETRE DECLARE — mesure conservatoire du 01/09/2026.
          ⚠️ UNE LISTE TROMPE PLUS QU'UN NOMBRE : le lecteur croit voir des GENS.
             Une liste intitulee « personnel » qui omet une population laisse
             croire que l'effectif est au complet. La mention reste donc ici,
             meme si ce n'est pas un total. */}
      <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
        Périmètre : agents de sécurité uniquement.
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        {renderSelectors()}
        <button className="gss-btn gss-btn-primary" onClick={handleGenerate} disabled={generating}>
          {generating ? 'Generation...' : 'Generer bulletins du mois'}
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Masse salariale</span>
            <div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg></div>
          </div>
          {/* ⚠️ CHIFFRE RETIRE le 01/09/2026 — « NOMMER L'ETAT PLUTOT QU'ANNOTER
              UNE FAUSSE VALEUR ». Cette tuile sommait `net_imposable`, une
              colonne GENEREE ET FAUSSE : elle affichait 50 400 pour un brut de
              62 500 (4 % CNSS seule, sur une assiette ignorant les rubriques).
              ⚠️ NE PAS se contenter d'une mention de perimetre ici : ecrire
              « agents uniquement » a cote d'un nombre faux ferait croire qu'il
              est juste POUR LES AGENTS. Aucun nombre = aucune tromperie.
              Le chiffre revient quand `net_imposable` est corrigee — voir
              CLAUDE.md, §Tuile « Total masse salariale ». */}
          <div className="stat-card-value" style={{ fontSize: 15, color: 'var(--gss-text-secondary)', lineHeight: 1.35 }}>
            Indisponible
            <div style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--gss-text-light)', marginTop: 2 }}>
              Calcul en cours de correction.
            </div>
          </div>
          <div className="stat-card-sub">{MOIS_LABELS[mois]} {annee}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Bulletins brouillon</span>
            <div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div>
          </div>
          <div className="stat-card-value">{stats.brouillon}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Bulletins valides</span>
            <div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
          </div>
          <div className="stat-card-value">{stats.valide}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Bulletins payes</span>
            <div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
          </div>
          <div className="stat-card-value">{stats.paye}</div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: 16 }}>
        {loadingBulletins ? (
          <p>Chargement...</p>
        ) : bulletins.length === 0 ? (
          <p>Aucun bulletin pour cette periode.</p>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th>Matricule</th>
                  <th>Agent</th>
                  <th>Base</th>
                  {/* ⚠️ COLONNE AJOUTÉE LE 07/10/2026 (lot B-bis). Elle dit les
                      jours retenus, leur base, et le détail par statut — sans
                      jamais dire ce que chaque statut PRODUIT en paie. */}
                  <th title="Jours retenus pour le calcul, la base de jours du pays, et le détail par statut tel que la pièce le porte">Jours</th>
                  <th>H.Sup</th>
                  <th>Risque</th>
                  <th>Nuit</th>
                  <th>Anciennete</th>
                  <th>Brut</th>
                  <th title="Retenues salariales : CNSS 4 % + INAM-AMU 5 %">Retenues</th>
                  <th title="Net imposable — assiette imposable moins retenues">Net imposable</th>
                  <th>IRPP</th>
                  <th title="Net a payer — brut moins retenues moins IRPP">NET A PAYER</th>
                  <th>Statut</th>
                  {/* 🔴 FILE 50 — LA PIECE DIT A QUEL TITRE ELLE A ETE POSEE.
                      Colonne ajoutee le 16/09/2026. Elle ne montre pas QUI a
                      agi (l audit le fait) mais AU TITRE DE QUEL POSTE. */}
                  <th title="Le ou les roles qui ont autorise le dernier acte pose sur la piece">Posé au titre de</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bulletins.map((b) => [(
                  <tr key={b.id}>
                    <td>{b.matricule}</td>
                    <td>{b.agent || `${b.prenom || ''} ${b.nom || ''}`.trim()}</td>
                    <td>{fmt(b.salaire_base)}</td>
                    <td style={{ whiteSpace: 'normal', minWidth: 120 }}>
                      {(() => {
                        const j = joursDe(b);
                        if (j.payes === null && j.detail.length === 0) {
                          return (
                            <span title="Pièce antérieure au lot : le détail des jours n'est pas enregistré, et il n'est pas reconstitué.">—</span>
                          );
                        }
                        return (
                          <>
                            <div style={{ fontWeight: 600 }}>
                              {j.payes === null ? '—' : j.payes}
                              {j.base === null ? '' : ` / ${j.base}`}
                              {j.nonPayes ? (
                                <span style={{ color: '#B91C1C', fontWeight: 400 }}>
                                  {` (−${j.nonPayes})`}
                                </span>
                              ) : null}
                            </div>
                            {j.detail.length > 0 && (
                              <div style={{ fontSize: 10, color: 'var(--gss-text-muted)', lineHeight: 1.4 }}
                                title={j.lus === null || j.lus === undefined ? undefined
                                  : `${j.lus} jour(s) de pointage lu(s) à l'émission`}>
                                {j.detail.join(' · ')}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td>{fmt(b.montant_heures_sup)}</td>
                    <td>{fmt(b.prime_risque)}</td>
                    <td>{Number(b.prime_nuit) > 0 ? fmt(b.prime_nuit) : '-'}</td>
                    <td>{Number(b.prime_anciennete) > 0 ? fmt(b.prime_anciennete) : '-'}</td>
                    {/* SOURCES NEUVES. `salaire_brut`, `cnss_employe` et
                        `net_imposable` sont des colonnes GENEREES DEPRECIEES ET
                        FAUSSES (4 %/16,5 % sur le brut entier) : elles ne sont
                        plus lues. « — » signale un bulletin de l'ancien moteur,
                        qui n'a pas ces valeurs. */}
                    <td>{b.salaire_brut_reel === null || b.salaire_brut_reel === undefined ? <span title="Bulletin de l'ancien moteur : valeur non disponible">—</span> : fmt(b.salaire_brut_reel)}</td>
                    <td>{fmt(b.total_charges_salariales)}</td>
                    <td>{fmt(b.net_imposable_moteur)}</td>
                    <td>{fmt(b.impot_revenu)}</td>
                    <td><strong>{fmt(b.net_a_payer)}</strong></td>
                    <td>
                      <span className={statutBadge(b.statut)}>
                        {b.statut === 'paye' ? 'Paye' : b.statut === 'valide' ? 'Valide' : 'Brouillon'}
                      </span>
                    </td>
                    <td>{titreDeLActe(b)}</td>
                    <td>
                      <div className="gss-actions">
                        <button className="gss-btn gss-btn-sm" title="Imprimer le bulletin (PDF)"
                          disabled={impression === b.id}
                          onClick={() => handlePrint(b.id)}>
                          {impression === b.id ? '…' : 'Imprimer'}
                        </button>
                        {b.document_url && (
                          <BoutonTelechargerDocument url={urlDocument('bulletin', b.id)} format="PDF" />
                        )}
                        {b.statut === 'brouillon' && (
                          <>
                            <button className="gss-btn-icon" title="Modifier" onClick={() => openEdit(b)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 3a2.85 2.85 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                              </svg>
                            </button>
                            <button className="gss-btn gss-btn-sm" onClick={() => handleValidate(b.id)}>Valider</button>
                          </>
                        )}
                        {b.statut === 'valide' && (
                          <button className="gss-btn gss-btn-sm gss-btn-primary" onClick={() => handlePay(b.id)}>Payer</button>
                        )}
                        <button
                          className="gss-btn gss-btn-sm"
                          style={{ fontSize: 11, background: 'linear-gradient(135deg, #8B1A1A, #C8A84E)', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                          onClick={() => setExplainBulletin(b)}
                        >
                          Expliquer IA
                        </button>
                      </div>
                    </td>
                  </tr>
                ), constatsDe(b).length > 0 && (
                  /* 🔎 LE DIAGNOSTIC EST ICI, ET PLUS SUR LE BULLETIN
                   *    (decision de Dom, 15/09/2026).
                   * ⚠️ Le PDF portait un bloc « MENTIONS OBLIGATOIRES — DONNEES
                   *    MANQUANTES » qui melangeait deux natures : ce que la loi
                   *    exige d'un bulletin, et du diagnostic. Un bulletin est
                   *    destine au salarie et au controleur, pas a l'operateur.
                   *    Le diagnostic remonte donc a l'ecran QUI GENERE.
                   * ⓘ La donnee, elle, reste CONSERVEE sur la piece
                   *   (`bulletins_paie.reserves`) : c'est la meme source, lue a
                   *   un autre endroit — jamais une seconde verite. */
                  <tr key={`${b.id}-constats`} className="gss-row-constats">
                    {/* ⓘ 14 -> 15 le 07/10/2026 : la colonne « Jours » a rejoint
                        l'en-tête, et un `colSpan` qui ne suit pas laisse une
                        cellule orpheline en bout de ligne. */}
                    <td colSpan={15} style={{ padding: '4px 10px 8px 10px', background: '#FAFAFA' }}>
                      {constatsDe(b).map((c, i) => (
                        <div key={i} style={{ fontSize: 11, color: '#555', lineHeight: 1.5 }}>
                          <span style={{ color: '#8B7500', marginRight: 6 }}>◆</span>
                          {c.message}
                          {c.constate_le && (
                            <span style={{ color: '#999', marginLeft: 6 }}>
                              (constate au {String(c.constate_le).slice(0, 10)})
                            </span>
                          )}
                        </div>
                      ))}
                    </td>
                  </tr>
                )])}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  // ==================== TAB 2: LIVRE DE PAIE ====================
  const renderLivre = () => {
    const agents = livre?.agents || livre?.lignes || [];
    const summary = livre?.summary || livre || {};
    return (
      <>
        {renderSelectors()}

        <div className="content-card" style={{ marginTop: 16 }}>
          <h3 className="content-card-title">Recapitulatif - {MOIS_LABELS[mois]} {annee}</h3>
          {/* ⚠️ PERIMETRE DECLARE — mesure conservatoire du 01/09/2026. Cet etat
              ne lit QUE les agents (JOIN ... ON b.agent_id) : il exclut
              structurellement le personnel administratif. Le chiffre reste
              incomplet, il CESSE de se faire passer pour complet. */}
          <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
            Périmètre : agents de sécurité uniquement. Le personnel administratif ne figure pas dans ce livre.
          </div>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">Total Brut</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg></div></div>
              <div className="stat-card-value">{fmt(summary.total_brut)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">CNSS Employe (4%)</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg></div></div>
              <div className="stat-card-value">{fmt(summary.total_cnss_employe)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">CNSS Employeur (16.5%)</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg></div></div>
              <div className="stat-card-value">{fmt(summary.total_cnss_employeur)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">Total net imposable</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg></div></div>
              <div className="stat-card-value">{fmt(summary.total_net_imposable)}</div>
              {/* MENTION EXPLICITE — l'IRPP n'est PAS calcule par le systeme.
                  Sans elle, « net » se lit « net a payer » et le bulletin devient
                  trompeur pour le salarie. Meme famille que le bouton qui
                  affirmait un envoi qu'il n'avait pas fait. */}
              <div className="stat-card-sub">avant retenue IRPP</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><span className="stat-card-label">Nb agents</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg></div></div>
              <div className="stat-card-value">{summary.nb_agents || agents.length}</div>
            </div>
          </div>
        </div>

        {/* IA Ecarts paie */}
        {agents.length > 0 && (
          <div className="content-card" style={{ marginTop: 16 }}>
            <IAAnalysisPanel
              type="ecarts-paie"
              data={{ mois, annee }}
              buttonLabel="Verifier les ecarts avec l'IA"
              disclaimer="Analyse indicative generee par IA a partir des donnees du bulletin. Ne constitue pas un avis comptable — a verifier par un professionnel."
            />
          </div>
        )}

        <div className="content-card" style={{ marginTop: 16 }}>
          {loadingLivre ? (
            <p>Chargement...</p>
          ) : agents.length === 0 ? (
            <p>Aucune donnee pour cette periode.</p>
          ) : (
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead>
                  <tr>
                    <th>Matricule</th>
                    <th>Agent</th>
                    <th>Salaire base</th>
                    <th>H.Sup</th>
                    <th>Primes</th>
                    <th>Deductions</th>
                    <th>Brut</th>
                    <th>CNSS Employe (4%)</th>
                    <th>CNSS Employeur (16.5%)</th>
                    <th title="Net imposable — avant retenue IRPP">Net imposable</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a, i) => (
                    <tr key={a.id || i}>
                      <td>{a.matricule}</td>
                      <td>{a.agent || `${a.prenom || ''} ${a.nom || ''}`.trim()}</td>
                      <td>{fmt(a.salaire_base)}</td>
                      <td>{fmt(a.montant_heures_sup)}</td>
                      <td>{fmt(a.primes)}</td>
                      <td>{fmt(a.deductions)}</td>
                      <td>{fmt(a.salaire_brut)}</td>
                      <td>{fmt(a.cnss_employe)}</td>
                      <td>{fmt(a.cnss_employeur)}</td>
                      <td><strong>{fmt(a.net_imposable)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    );
  };

  // ==================== TAB 3: DECLARATION CNSS ====================
  const renderCnss = () => {
    const lignes = cnss?.lignes || cnss?.agents || [];
    const totals = cnss?.totaux || cnss?.totals || {};
    const totalBrut = totals.total_brut || lignes.reduce((s, l) => s + Number(l.salaire_brut || l.brut || 0), 0);
    const totalCnssEmp = totals.total_cnss_employe || lignes.reduce((s, l) => s + Number(l.cnss_employe || 0), 0);
    const totalCnssEr = totals.total_cnss_employeur || lignes.reduce((s, l) => s + Number(l.cnss_employeur || 0), 0);

    return (
      <>
        {renderSelectors()}

        <div className="content-card" style={{ marginTop: 16 }}>
          <h3 className="content-card-title">Declaration CNSS - {MOIS_LABELS[mois]} {annee}</h3>
          {/* ⚠️ PERIMETRE DECLARE — mesure conservatoire du 01/09/2026. Cet etat
              ne lit QUE les agents (JOIN ... ON b.agent_id) : il exclut
              structurellement le personnel administratif. Le chiffre reste
              incomplet, il CESSE de se faire passer pour complet. */}
          <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
            Périmètre : agents de sécurité uniquement. Le personnel administratif n'est pas inclus dans cette déclaration.
          </div>
          {/* Le numero employeur vient de la ligne du tenant (API), il n'est
              plus ecrit en dur : « 183821 » etait le numero de l'EDITEUR,
              affiche a tous les tenants sur une PIECE LEGALE. Absent = on le
              DIT — un champ vide se lirait comme un oubli d'affichage, et un
              numero plausible mais faux se deposerait tel quel. */}
          {cnss?.numero_cnss ? (
            <p style={{ marginBottom: 16, fontWeight: 600 }}>N. CNSS Employeur : {cnss.numero_cnss}</p>
          ) : (
            <p style={{ marginBottom: 16, fontWeight: 600, color: '#B45309' }}>
              N. CNSS Employeur : non renseigne — a saisir dans Parametres avant tout depot.
            </p>
          )}

          {loadingCnss ? (
            <p>Chargement...</p>
          ) : lignes.length === 0 ? (
            <p>Aucune donnee pour cette periode.</p>
          ) : (
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Salaire brut</th>
                    <th>CNSS Employe (4%)</th>
                    <th>CNSS Employeur (16.5%)</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l, i) => (
                    <tr key={l.id || i}>
                      <td>{l.agent || `${l.prenom || ''} ${l.nom || ''}`.trim()}</td>
                      <td>{fmt(l.salaire_brut || l.brut)}</td>
                      <td>{fmt(l.cnss_employe)}</td>
                      <td>{fmt(l.cnss_employeur)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700, backgroundColor: 'rgba(255,255,255,0.05)' }}>
                    <td>TOTAUX</td>
                    <td>{fmt(totalBrut)}</td>
                    <td>{fmt(totalCnssEmp)}</td>
                    <td>{fmt(totalCnssEr)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    );
  };

  // ==================== TAB 4: ALERTES RH ====================
  const renderAlertes = () => {
    const grouped = {};
    alertes.forEach(a => {
      if (!grouped[a.type]) grouped[a.type] = [];
      grouped[a.type].push(a);
    });

    const severityStyle = (severity) => ({
      borderLeft: `4px solid ${severity === 'critical' ? '#C62828' : '#E65100'}`,
      backgroundColor: severity === 'critical' ? '#FFF5F5' : '#FFF8E1',
      padding: '12px 16px',
      marginBottom: 8,
      borderRadius: 4,
    });

    const severityBadge = (severity) => ({
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 700,
      color: '#FFF',
      backgroundColor: severity === 'critical' ? '#C62828' : '#E65100',
      marginLeft: 8,
    });

    return (
      <div className="content-card" style={{ marginTop: 0 }}>
        <h3 className="content-card-title">Alertes Code du Travail</h3>

        {loadingAlertes ? (
          <p>Chargement...</p>
        ) : alertes.length === 0 ? (
          <p style={{ color: '#4CAF50', fontWeight: 600 }}>Aucune alerte — conformite OK</p>
        ) : (
          Object.entries(grouped).map(([type, items]) => (
            <div key={type} style={{ marginBottom: 24 }}>
              <h4 style={{ marginBottom: 8, fontSize: 14, color: '#333' }}>
                {ALERT_TYPE_LABELS[type] || type}
                <span style={{ marginLeft: 8, color: '#666', fontWeight: 400 }}>({items.length})</span>
              </h4>
              {items.map((alerte, i) => (
                <div key={i} style={severityStyle(alerte.severity)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <strong>{alerte.agent_prenom} {alerte.agent_nom}</strong>
                      <span style={{ color: '#666', marginLeft: 8, fontSize: 13 }}>{alerte.matricule}</span>
                      <span style={severityBadge(alerte.severity)}>
                        {alerte.severity === 'critical' ? 'CRITIQUE' : 'ATTENTION'}
                      </span>
                    </div>
                    {alerte.date_echeance && (
                      <span style={{ fontSize: 13, color: '#555' }}>
                        Echeance : {new Date(alerte.date_echeance).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, color: '#444' }}>{alerte.message}</div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div>
{error && <div className="gss-alert gss-alert-error">{error}</div>}

      {/* Tab buttons */}
      <div className="gss-toolbar" style={{ marginBottom: 16, gap: 8 }}>
        <button
          className={`gss-btn ${tab === 'bulletins' ? 'gss-btn-primary' : ''}`}
          onClick={() => setTab('bulletins')}
        >
          Bulletins de paie
        </button>
        <button
          className={`gss-btn ${tab === 'livre' ? 'gss-btn-primary' : ''}`}
          onClick={() => setTab('livre')}
        >
          Livre de paie
        </button>
        <button
          className={`gss-btn ${tab === 'cnss' ? 'gss-btn-primary' : ''}`}
          onClick={() => setTab('cnss')}
        >
          Declaration CNSS
        </button>
        <button
          className={`gss-btn ${tab === 'alertes' ? 'gss-btn-primary' : ''}`}
          onClick={() => setTab('alertes')}
          style={{ position: 'relative' }}
        >
          Alertes RH
          {alerteCount > 0 && (
            <span style={{
              position: 'absolute', top: -6, right: -6,
              backgroundColor: '#C62828', color: '#FFF',
              borderRadius: '50%', minWidth: 20, height: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, padding: '0 4px',
            }}>
              {alerteCount}
            </span>
          )}
        </button>
      </div>

      {/* Tab content */}
      {tab === 'bulletins' && renderBulletins()}
      {tab === 'livre' && renderLivre()}
      {tab === 'cnss' && renderCnss()}
      {tab === 'alertes' && renderAlertes()}

      {/* IA Explain Modal */}
      {explainBulletin && (
        <div className="gss-modal-overlay" onClick={() => setExplainBulletin(null)}>
          <div className="gss-modal gss-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="gss-modal-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C8A84E" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              <div>
                <h2>Assistant GSS</h2>
                <div className="gss-modal-subtitle">{explainBulletin.agent || `${explainBulletin.prenom || ''} ${explainBulletin.nom || ''}`.trim()} — {explainBulletin.matricule}</div>
              </div>
              <button className="gss-modal-close" onClick={() => setExplainBulletin(null)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem', maxHeight: '60vh', overflowY: 'auto' }}>
              <IAAnalysisPanel
                key={explainBulletin.id}
                type="bulletin"
                data={{ bulletin_id: explainBulletin.id }}
                buttonLabel="Analyser ce bulletin avec l'IA"
                disclaimer="Analyse indicative generee par IA a partir des donnees du bulletin. Ne constitue pas un avis comptable — a verifier par un professionnel."
                autoAnalyze
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="gss-modal-overlay" onClick={() => setEditModal(null)}>
          <div className="gss-modal gss-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="gss-modal-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M17 3a2.85 2.85 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
              <div>
                <h2>Modifier bulletin - {editModal.agent || `${editModal.prenom || ''} ${editModal.nom || ''}`.trim()}</h2>
                <div className="gss-modal-subtitle">Ajustement des elements de paie</div>
              </div>
              <button className="gss-modal-close" onClick={() => setEditModal(null)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                <span>HEURES SUPPLEMENTAIRES</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label>Nombre d'heures sup</label>
                  <input
                    type="number"
                    className="gss-input"
                    value={editForm.nb_heures_sup}
                    onChange={(e) => setEditForm({ ...editForm, nb_heures_sup: e.target.value })}
                    min="0"
                    step="0.5"
                  />
                </div>
              </div>
              {editNbHS > 0 && (
                <div style={{ margin: '8px 0 16px', padding: '10px 12px', backgroundColor: '#F5F5F5', borderRadius: 4, fontSize: 13, color: '#444' }}>
                  <div>Taux horaire de base : <strong>{fmt(editTauxHoraire)}</strong></div>
                  <div>1-8h : taux +15% = {fmt(Math.round(editTauxHoraire * 1.15))} /h</div>
                  {editNbHS > 8 && <div>Au-dela 8h : taux +35% = {fmt(Math.round(editTauxHoraire * 1.35))} /h</div>}
                  <div style={{ marginTop: 4, fontWeight: 600 }}>Montant heures sup : <strong>{fmt(editMontantHS)}</strong></div>
                </div>
              )}

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                </svg>
                <span>PRIMES & INDEMNITES</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label>Primes (FCFA)</label>
                  <input
                    type="number"
                    className="gss-input"
                    value={editForm.primes}
                    onChange={(e) => setEditForm({ ...editForm, primes: e.target.value })}
                    min="0"
                  />
                </div>
                <div className="gss-field">
                  <label>Prime de risque (FCFA)</label>
                  <input
                    type="number"
                    className="gss-input"
                    value={editForm.prime_risque}
                    onChange={(e) => setEditForm({ ...editForm, prime_risque: e.target.value })}
                    min="0"
                  />
                  <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>Standard: 5 000 FCFA/mois</div>
                </div>
                <div className="gss-field">
                  <label>Prime de nuit (FCFA)</label>
                  <input
                    type="number"
                    className="gss-input"
                    value={editForm.prime_nuit}
                    onChange={(e) => setEditForm({ ...editForm, prime_nuit: e.target.value })}
                    min="0"
                  />
                  <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>Standard: 8 750 FCFA/mois si poste nuit</div>
                </div>
                <div className="gss-field">
                  <label>Prime anciennete (FCFA)</label>
                  <input
                    type="number"
                    className="gss-input"
                    value={editForm.prime_anciennete}
                    onChange={(e) => setEditForm({ ...editForm, prime_anciennete: e.target.value })}
                    min="0"
                  />
                  <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>3%/6%/9%/12% selon anciennete (3/6/9/12 ans)</div>
                </div>
                <div className="gss-field">
                  <label>Panier nuit (FCFA)</label>
                  <input
                    type="number"
                    className="gss-input"
                    value={editForm.panier_nuit}
                    onChange={(e) => setEditForm({ ...editForm, panier_nuit: e.target.value })}
                    min="0"
                  />
                </div>
              </div>

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
                </svg>
                <span>DEDUCTIONS</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label>Deductions (FCFA)</label>
                  <input
                    type="number"
                    className="gss-input"
                    value={editForm.deductions}
                    onChange={(e) => setEditForm({ ...editForm, deductions: e.target.value })}
                    min="0"
                  />
                </div>
              </div>
            </div>
            {/* Simulation IA */}
            {simulationAfter && simulationBefore && (
              <div style={{ padding: '0 1.5rem 1rem' }}>
                <IAAnalysisPanel
                  type="simulation-paie"
                  data={{ bulletin_id: editModal.id, avant: simulationBefore, apres: simulationAfter }}
                  buttonLabel="Expliquer l'impact avec l'IA"
                  disclaimer="Analyse indicative generee par IA a partir des donnees du bulletin. Ne constitue pas un avis comptable — a verifier par un professionnel."
                />
              </div>
            )}
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => { setEditModal(null); setSimulationAfter(null); }}>Fermer</button>
              <button className="gss-btn-deploy" onClick={handleEditSave}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
